import http, { request } from './http';
import { getCachedMemberCandidateTree } from '@/utils/sessionOrgCache';
import type { TaskItem } from './taskApi';

/** 阶段计划交付日期（与后端 PhaseDeliveryDTO 对应） */
export interface PhaseDeliveryDTO {
  phaseCode: string;
  phaseName: string;
  plannedEndTime?: string | null;
}

export interface Project {
  id: number;
  name: string;
  /** 项目编号 */
  projectCode?: string | null;
  status: string;
  /** 客户名称 */
  owner?: string | null;
  /** 项目整体交付日期 */
  projectDeliveryDate?: string | null;
  /** 当前阶段编码：CONCEPT, PLAN, DEVELOP, VERIFY, RELEASE */
  currentPhaseCode?: string | null;
  /** 当前阶段名称：概念、计划、开发、验证、发布 */
  currentPhaseName?: string | null;
  /** 各阶段计划交付日期 */
  phaseDeliveryDates?: PhaseDeliveryDTO[] | null;
  /** 项目进度 0-100 */
  progress?: number | null;
  /** 项目简介 / 立项背景摘要 */
  description?: string | null;
  /** 风险等级：HIGH / MEDIUM / LOW */
  riskLevel?: string | null;
  /** 我的交付日期（当前阶段计划结束或待办截止） */
  myDeliveryDate?: string | null;
  projectLeadUserId?: number | null;
  projectMemberUserIds?: number[] | null;
  creatorUserId?: number | null;
  phasePlans?: ProjectPhasePlanItem[] | null;
}

export interface CreateProjectIssueTrackItem {
  issueSummary: string;
  priority?: string | null;
  expectedResolveBy?: string | null;
  assigneeUserId?: number | null;
  /** open | close | 延期 | 挂起 */
  status?: string | null;
  issueCategory?: string | null;
  issueAttachmentUrl?: string | null;
  postResolutionAttachmentUrl?: string | null;
  /** AI_CHAT | TR_REVIEW | PEER_REVIEW | MANUAL */
  issueSource?: string | null;
  solutionRemarks?: string | null;
}

export interface WbsTemplateRef {
  knowledgeDocId?: string;
  knowledgeBaseId?: string;
  title?: string;
  version?: string;
}

export interface WbsTemplateCandidate {
  knowledgeDocId?: string;
  knowledgeBaseId?: string;
  title?: string;
  fileName?: string;
  version?: string;
  hasHoursConfig?: boolean;
}

export interface CreateProjectPayload {
  /**
   * 项目名称
   */
  name: string;
  /**
   * 项目编号
   */
  projectCode?: string | null;
  /**
   * 客户名称
   */
  owner?: string | null;
  /**
   * 项目简介（对应详情页「项目简介」）
   */
  description?: string | null;
  /**
   * 项目整体交付日期，格式：YYYY-MM-DD
   */
  projectDeliveryDate?: string | null;
  /** 可选，可在项目详情补全后再推进到计划阶段 */
  projectLeadUserId?: number | null;
  projectMemberUserIds?: number[];
  phasePlans?: ProjectPhasePlanItem[];
  /** AI 问题列表：创建时同步至问题清单 */
  issueTrackItems?: CreateProjectIssueTrackItem[];
  /** 创建项目页 AI 会话 ID，用于迁移会话与 Token 至项目空间 */
  aiConversationId?: string | null;
  createSource?: 'HOME' | 'YINO';
  confirmDuplicateName?: boolean;
  /** 可选：知识库 WBS 模板；未选则 Agent 默认规则 */
  wbsTemplateRef?: WbsTemplateRef | null;
}

export interface ProjectMemberOption {
  userId: number;
  realName: string;
  /** 通讯录岗位/职位，供创建项目 AI 匹配阶段负责人 */
  position?: string | null;
  departmentName?: string | null;
  platformUserId?: string | null;
  /** 飞书/企微头像展示 URL */
  avatarUrl?: string | null;
}

export interface ProjectMemberTreeNode {
  value: number;
  label: string;
  disabled?: boolean;
  children?: ProjectMemberTreeNode[];
}

export interface MemberPickerDeptNode {
  deptId: number;
  name: string;
  hasChildren?: boolean;
}

export interface MemberPickerDefaultScope {
  scopeDeptId?: number | null;
  scopeDeptName?: string | null;
  scopeDeptPath?: string | null;
  members: ProjectMemberOption[];
}

export interface ProjectPhasePlanItem {
  phaseCode: string;
  phaseName: string;
  phaseOwnerUserId?: number | null;
  plannedEndDate?: string | null;
  /** AI 建议的阶段负责人在本阶段的职责（仅展示，可不提交后端） */
  phaseDuty?: string | null;
}

export interface PageResult<T> {
  records: T[];
  total: number;
  page: number;
  size: number;
}

export interface ProjectQuery {
  page?: number;
  size?: number;
}

export async function getProjects(params: ProjectQuery = {}): Promise<PageResult<Project>> {
  return request<PageResult<Project>>(http.get('/projects', { params }));
}

export async function suspendProjects(projectIds: number[], reason?: string) {
  return request(http.post('/projects/lifecycle/suspend', { projectIds, reason }));
}

export async function resumeProjects(projectIds: number[], deliveryDate: string, reason?: string) {
  return request(http.post('/projects/lifecycle/resume', { projectIds, deliveryDate, reason }));
}

export async function voidProjects(projectIds: number[], reason?: string) {
  return request(http.post('/projects/lifecycle/void', { projectIds, reason }));
}


export interface ProjectAccessOption {
  id: number;
  name: string;
  role?: string | null;
  progress?: number | null;
  status?: string | null;
  /** 用于下拉「最新在前」排序 */
  createdAt?: string | null;
  updatedAt?: string | null;
}

export async function getAccessibleProjects(params: ProjectQuery = {}): Promise<ProjectAccessOption[]> {
  const res = await getProjects({ page: params.page ?? 1, size: params.size ?? 200 });
  const list = (res.records || []).map((p) => ({
    id: p.id,
    name: p.name,
    role: p.status || '成员',
    progress: typeof p.progress === 'number' ? p.progress : 0,
    status: p.status,
    createdAt: (p as { createdAt?: string | null }).createdAt ?? null,
    updatedAt: (p as { updatedAt?: string | null }).updatedAt ?? null,
  }));
  // 后端已按创建时间倒序；再兜底一次，最新在前
  return list.sort((a, b) => {
    const ta = Date.parse(String(a.updatedAt || a.createdAt || '')) || 0;
    const tb = Date.parse(String(b.updatedAt || b.createdAt || '')) || 0;
    if (tb !== ta) return tb - ta;
    return Number(b.id) - Number(a.id);
  });
}

export async function getProject(id: number): Promise<Project> {
  return request<Project>(http.get(`/projects/${id}`));
}

/** 团队编辑轻量上下文（不含 SOR 分析等大字段） */
export interface ProjectTeamContext {
  id: number;
  projectCode?: string | null;
  owner?: string | null;
  description?: string | null;
  projectDeliveryDate?: string | null;
  projectLeadUserId?: number | null;
  creatorUserId?: number | null;
  projectMemberUserIds?: number[] | null;
  phasePlans?: ProjectPhasePlanItem[] | null;
}

export async function getProjectTeamContext(id: number): Promise<ProjectTeamContext> {
  return request<ProjectTeamContext>(http.get(`/projects/${id}/team-context`));
}

export interface ProjectSorInfo {
  objectKey?: string;
  fileName?: string;
  accessUrl?: string;
  /** 已保存的需求分析 JSON 文本（应为数组 JSON；也可能被历史客户端写成双层字符串） */
  analysisJson?: string;
  /** 需求清单乐观锁版本 */
  analysisRevision?: number | string;
}

export interface RequirementEditLockState {
  locked: boolean;
  mine: boolean;
  mineSession?: boolean;
  editorUserId?: number | null;
  editorUserName?: string | null;
  sessionId?: string | null;
  expiresAt?: string | null;
}

function unwrapRequirementEditLock(response: any): RequirementEditLockState {
  if (!response?.success || !response?.data) throw new Error(response?.message || '需求清单编辑锁操作失败');
  return response.data as RequirementEditLockState;
}

export async function getRequirementEditLock(projectId: number, sessionId?: string): Promise<RequirementEditLockState> {
  return unwrapRequirementEditLock(
    await request<any>(
      http.get(`/projects/${projectId}/requirements/edit-lock`, {
        params: sessionId ? { sessionId } : undefined,
      }),
    ),
  );
}

export async function acquireRequirementEditLock(projectId: number, sessionId: string): Promise<RequirementEditLockState> {
  return unwrapRequirementEditLock(await request<any>(http.post(`/projects/${projectId}/requirements/edit-lock`, { sessionId })));
}

export async function heartbeatRequirementEditLock(projectId: number, sessionId: string): Promise<RequirementEditLockState> {
  return unwrapRequirementEditLock(await request<any>(http.put(`/projects/${projectId}/requirements/edit-lock`, { sessionId })));
}

export async function releaseRequirementEditLock(projectId: number, sessionId: string): Promise<void> {
  const response = await request<any>(http.delete(`/projects/${projectId}/requirements/edit-lock`, { data: { sessionId } }));
  if (!response?.success) throw new Error(response?.message || '退出编辑失败');
}

export async function forceReleaseRequirementEditLock(projectId: number): Promise<void> {
  const response = await request<any>(http.delete(`/projects/${projectId}/requirements/edit-lock/force`));
  if (!response?.success) throw new Error(response?.message || '强制解锁失败');
}

export async function getProjectSor(projectId: number): Promise<ProjectSorInfo> {
  const res = await request<any>(http.get(`/projects/${projectId}/sor`));
  // 后端 Result 包裹：{ success, data }
  return (res as any)?.data ?? {};
}

export async function setProjectActiveSor(
  projectId: number,
  objectKey: string,
  fileName: string,
): Promise<ProjectSorInfo> {
  const res = await request<any>(
    http.put(`/projects/${projectId}/sor/active`, { objectKey, fileName }),
  );
  return (res as any)?.data ?? {};
}

export async function uploadProjectSor(
  projectId: number,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<ProjectSorInfo> {
  const form = new FormData();
  form.append('file', file);
  const res = await request<any>(
    http.post(`/projects/${projectId}/sor/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e: ProgressEvent) => {
        if (!onProgress || !e.total) return;
        onProgress(Math.max(0, Math.min(100, Math.round((e.loaded / e.total) * 100))));
      },
    }),
  );
  return (res as any)?.data ?? {};
}

/** 提交 SOR 分析任务，成功时返回 { data: { taskId } }，失败时返回 { success: false, message } */
export async function submitProjectSorAnalyzeTask(
  projectId: number,
  opts?: {
    requestId?: string;
    preflightOnly?: boolean;
    preflightTaskId?: string;
    references?: import('./sorGenerationApi').SorReferenceSelection[];
    knowledgeDocumentIds?: string[];
    knowledgeBaseIds?: string[];
    knowledgeFileNames?: string[];
    referenceKnowledgeContext?: string;
    fieldSchema?: string;
    ledgerWriteContext?: { embedded: boolean; targetBatchId: number; replaceBatchIds: number[]; appendMode: boolean };
    ledgerBaseRevision?: number;
  },
): Promise<{ success?: boolean; data?: { taskId: string }; message?: string }> {
  const body: Record<string, unknown> = {};
  body.requestId = opts?.requestId || crypto.randomUUID();
  if (opts?.preflightOnly) body.preflightOnly = true;
  if (opts?.preflightTaskId) body.preflightTaskId = opts.preflightTaskId;
  if (opts?.ledgerWriteContext) body.ledgerWriteContext = opts.ledgerWriteContext;
  if (opts?.ledgerBaseRevision != null) body.ledgerBaseRevision = opts.ledgerBaseRevision;
  if (opts?.references) body.references = opts.references;
  if (opts?.knowledgeDocumentIds?.length) body.knowledgeDocumentIds = opts.knowledgeDocumentIds;
  if (opts?.knowledgeBaseIds?.length) body.knowledgeBaseIds = opts.knowledgeBaseIds;
  if (opts?.referenceKnowledgeContext?.trim()) {
    body.referenceKnowledgeContext = opts.referenceKnowledgeContext.trim();
  }
  if (opts?.fieldSchema?.trim()) body.fieldSchema = opts.fieldSchema.trim();
  if (opts?.knowledgeFileNames?.length) body.knowledgeFileNames = opts.knowledgeFileNames;
  const res = await request<any>(
    http.post(`/projects/${projectId}/sor/analyze/task`, Object.keys(body).length ? body : undefined),
  );
  return res as any;
}

export async function pollProjectSorAnalyzeResult(taskId: string, projectId?: number): Promise<any> {
  const res = await request<any>(
    http.get(`/projects/sor/analyze/result/${encodeURIComponent(taskId)}`, {
      params:
        projectId != null && !Number.isNaN(Number(projectId)) ? { projectId: Number(projectId) } : undefined,
    }),
  );
  return res;
}

export type SorServerRecovery = {
  taskId: string; status: 'processing' | 'completed'; sourceObjectKey: string;
  baseRevision?: number; startedAt: number; requiresConfirmation: boolean;
  writeContext?: { embedded: boolean; targetBatchId: number; replaceBatchIds: number[]; appendMode: boolean; fullTableReplace: boolean };
};

export async function recoverProjectSorAnalyzeTask(projectId: number): Promise<SorServerRecovery | null> {
  const res = await request<{ success?: boolean; data?: SorServerRecovery; message?: string }>(
    http.get(`/projects/${projectId}/sor/analyze/recovery`));
  if (res?.success === false) throw new Error(res.message || '查询生成任务失败');
  return res?.data ?? null;
}

export async function saveProjectSorAnalysis(
  projectId: number,
  analysisJson: string,
  revision = 0,
  taskId?: string,
): Promise<{ analysisRevision: number }> {
  // 必须用 text/plain：若用默认 application/json，部分环境下根节点为数组时与 @RequestBody String 组合会导致存库内容异常（双层引号或非数组文本），刷新后无法解析。
  const res = await request<{ success?: boolean; data?: number; message?: string }>(
    http.post(`/projects/${projectId}/sor/analysis`, analysisJson, {
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      params: { revision, ...(taskId ? { taskId } : {}) },
    }),
  );
  if (res && res.success === false) {
    throw Object.assign(new Error(res.message || '保存失败'), { status: 400, message: res.message || '保存失败' });
  }
  const next = Number(res?.data);
  return { analysisRevision: Number.isFinite(next) ? next : revision + 1 };
}

export async function cancelProjectSorAnalyzeTask(projectId: number, taskId?: string): Promise<void> {
  await request<void>(http.post(`/projects/${projectId}/sor/analyze/cancel${taskId ? `/${encodeURIComponent(taskId)}` : ''}`));
}

export async function refreshProjectSorNestedRefs(
  projectId: number,
  body: { requirement: Record<string, unknown>; knownFiles?: string[]; revision: number; referenceCorrection?: string },
): Promise<Record<string, unknown>> {
  const res = await request<{ success?: boolean; data?: Record<string, unknown>; message?: string }>(
    http.post(`/projects/${projectId}/sor/nested-refresh`, {
      requirement: body.requirement,
      revision: body.revision,
      referenceCorrection: body.referenceCorrection,
      ...(body.knownFiles?.length ? { knownFiles: body.knownFiles } : {}),
    }),
  );
  if (res && res.success === false) {
    throw new Error(res.message || '刷新失败');
  }
  return (res?.data || {}) as Record<string, unknown>;
}

export function createProjectRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fallback below */
  }
  return 'create-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

export async function checkProjectNameDuplicate(name: string): Promise<boolean> {
  const res = await request<{ duplicate?: boolean }>(http.get('/projects/name-check', { params: { name } }));
  return !!(res as any)?.duplicate;
}

export async function createProject(
  payload: CreateProjectPayload,
  idempotencyKey?: string,
): Promise<Project> {
  const key = idempotencyKey?.trim();
  return request<Project>(
    http.post('/projects', payload, key ? { headers: { 'Idempotency-Key': key } } : undefined),
  );
}

const PROJECT_ORG_HTTP_TIMEOUT_MS = 120_000;
/** 对齐后端 ipdAgentRestTemplate 读超时（WBS 生成可能耗时数分钟） */
const PROJECT_WBS_HTTP_TIMEOUT_MS = 300_000;

export type ProjectMemberCandidateScope = 'project' | 'assignment';

export async function listTodoAssigneeCandidates(projectId: number): Promise<ProjectMemberOption[]> {
  return request<ProjectMemberOption[]>(
    http.get(`/projects/${projectId}/todo-assignee-candidates`, { timeout: PROJECT_ORG_HTTP_TIMEOUT_MS }),
  );
}

export async function listProjectMemberCandidates(
  keyword = '',
  includeUserIds?: number[],
  scope: ProjectMemberCandidateScope = 'project',
): Promise<ProjectMemberOption[]> {
  const params: Record<string, string> = { keyword: keyword ?? '' };
  if (scope === 'assignment') params.scope = scope;
  if (includeUserIds?.length) {
    const ids = [...new Set(includeUserIds.map((n) => Number(n)).filter((n) => !Number.isNaN(n) && n > 0))];
    if (ids.length) params.includeUserIds = ids.join(',');
  }
  return request<ProjectMemberOption[]>(
    http.get('/projects/member-candidates', { params, timeout: PROJECT_ORG_HTTP_TIMEOUT_MS }),
  );
}

export async function listMemberPickerDeptChildren(parentDeptId?: number | null): Promise<MemberPickerDeptNode[]> {
  const params: Record<string, string | number> = {};
  if (parentDeptId != null && parentDeptId > 0) params.parentDeptId = parentDeptId;
  return request<MemberPickerDeptNode[]>(
    http.get('/projects/member-candidates/dept-children', { params, timeout: PROJECT_ORG_HTTP_TIMEOUT_MS }),
  );
}

export async function listMemberPickerDeptMembers(
  deptId: number,
  includeSubtree = true,
): Promise<ProjectMemberOption[]> {
  return request<ProjectMemberOption[]>(
    http.get('/projects/member-candidates/dept-members', {
      params: { deptId, includeSubtree },
      timeout: PROJECT_ORG_HTTP_TIMEOUT_MS,
    }),
  );
}

export async function listMemberPickerDefaultScope(): Promise<MemberPickerDefaultScope> {
  return request<MemberPickerDefaultScope>(
    http.get('/projects/member-candidates/default-scope', { timeout: PROJECT_ORG_HTTP_TIMEOUT_MS }),
  );
}

export async function listProjectMemberCandidateTree(): Promise<ProjectMemberTreeNode[]> {
  return getCachedMemberCandidateTree(() =>
    request<ProjectMemberTreeNode[]>(
      http.get('/projects/member-candidates/tree', { timeout: PROJECT_ORG_HTTP_TIMEOUT_MS }),
    ),
  );
}

/**
 * includePhaseOwners defaults to true for existing project-detail callers;
 * the project-create page passes false so historical owners are not auto-filled.
 */
export async function getProjectCreateDefaults(
  projectDeliveryDate?: string | null,
  includePhaseOwners = true,
): Promise<ProjectPhasePlanItem[]> {
  return request<ProjectPhasePlanItem[]>(
    http.get('/projects/create-defaults', {
      params: { projectDeliveryDate: projectDeliveryDate || undefined, includePhaseOwners },
      timeout: PROJECT_ORG_HTTP_TIMEOUT_MS,
    }),
  );
}

export interface DashboardData {
  projectId: number;
  requirementCount: number;
  designTaskCount: number;
  resourceUsageRate: number;
  progress: number;
}

export async function getProjectDashboard(id: number): Promise<DashboardData> {
  return request<DashboardData>(http.get(`/projects/${id}/dashboard`));
}

/** 与后端 Result 一致 */
interface ApiResult<T> {
  success: boolean;
  message?: string;
  data: T;
}

function assertOk<T>(res: ApiResult<T>): void {
  if (!res || !res.success) {
    throw new Error((res as ApiResult<T>)?.message || '操作失败');
  }
}

export interface ProjectAuditLog {
  id: number;
  projectId: number;
  eventType: string;
  actorUserId?: number | null;
  actorUserName?: string | null;
  detailJson?: string | null;
  detailSummary?: string | null;
  createdAt?: string | null;
}

export interface SaveProjectFieldsPayload {
  /** 非 null 时更新项目名称 */
  name?: string | null;
  description?: string | null;
  owner?: string | null;
  projectCode?: string | null;
  /** YYYY-MM-DD */
  projectDeliveryDate?: string | null;
  projectLeadUserId?: number | null;
  projectMemberUserIds?: number[] | null;
  phasePlans?: ProjectPhasePlanItem[] | null;
}

const workflowBase = (projectId: number) => `/projects/${projectId}/workflow`;

export async function getProjectWorkflowAuditLogs(projectId: number): Promise<ProjectAuditLog[]> {
  const res = await request<ApiResult<ProjectAuditLog[]>>(http.get(`${workflowBase(projectId)}/audit-logs`));
  assertOk(res);
  return res.data ?? [];
}

export async function advanceProjectToPlan(projectId: number): Promise<void> {
  const res = await request<ApiResult<unknown>>(http.post(`${workflowBase(projectId)}/advance-to-plan`));
  assertOk(res);
}

export async function freezeProjectVersion(projectId: number): Promise<void> {
  const res = await request<ApiResult<unknown>>(http.post(`${workflowBase(projectId)}/freeze-version`));
  assertOk(res);
}

export interface ApprovePhaseReviewPayload {
  nextPhaseCode?: string | null;
  nextPhaseOwnerUserId?: number | null;
  /** YYYY-MM-DD */
  nextPhasePlannedEndDate?: string | null;
}

export async function approveProjectFreeze(
  projectId: number,
  payload?: ApprovePhaseReviewPayload | null,
): Promise<void> {
  const res = await request<ApiResult<unknown>>(
    http.post(`${workflowBase(projectId)}/freeze-version/approve`, payload ?? {}),
  );
  assertOk(res);
}

export async function rejectProjectFreeze(projectId: number, reason: string): Promise<void> {
  const res = await request<ApiResult<unknown>>(http.post(`${workflowBase(projectId)}/freeze-version/reject`, { reason }));
  assertOk(res);
}

export async function submitProjectToProductManagers(
  projectId: number,
): Promise<{ notifiedCount: number; message: string }> {
  const res = await request<ApiResult<number>>(http.post(`${workflowBase(projectId)}/submit-to-pms`));
  assertOk(res);
  return { notifiedCount: res.data ?? 0, message: res.message || 'success' };
}

export async function shareProject(
  projectId: number,
  payload: { userIds: number[]; message?: string | null },
): Promise<void> {
  const res = await request<ApiResult<unknown>>(http.post(`${workflowBase(projectId)}/share`, payload));
  assertOk(res);
}

export async function saveProjectFields(projectId: number, payload: SaveProjectFieldsPayload): Promise<void> {
  const res = await request<ApiResult<unknown>>(http.post(`${workflowBase(projectId)}/save`, payload));
  assertOk(res);
}

/** 项目成员在该项目下未完成待办/问题条数（移除成员前二次确认） */
export async function getProjectMemberOpenTaskCount(projectId: number, userId: number): Promise<number> {
  const res = await request<ApiResult<number>>(
    http.get(`${workflowBase(projectId)}/member-open-task-count`, { params: { userId } }),
  );
  assertOk(res);
  return typeof res.data === 'number' ? res.data : 0;
}

export interface MemberOpenTaskReassignResult {
  reassignedCount: number;
}

/** 将成员未完成待办转交项目负责人并发送飞书/企微通知 */
export async function reassignMemberOpenTasksToLead(
  projectId: number,
  fromUserId: number,
): Promise<MemberOpenTaskReassignResult> {
  const res = await request<ApiResult<MemberOpenTaskReassignResult>>(
    http.post(`${workflowBase(projectId)}/reassign-member-open-tasks`, { fromUserId }),
  );
  assertOk(res);
  return (res.data as MemberOpenTaskReassignResult) ?? { reassignedCount: 0 };
}

export interface MemberOpenItem {
  id: number;
  title?: string | null;
  itemType?: string | null;
  status?: string | null;
  phaseCode?: string | null;
  taskCode?: string | null;
  wbsOwnerRole?: string | null;
  assigneeUserIds?: number[] | null;
  ownerUserId?: number | null;
}

export interface MemberOpenItems {
  tasks: MemberOpenItem[];
  issues: MemberOpenItem[];
}

export async function getProjectMemberOpenItems(
  projectId: number,
  userId: number,
): Promise<MemberOpenItems> {
  const res = await request<ApiResult<MemberOpenItems>>(
    http.get(`${workflowBase(projectId)}/member-open-items`, { params: { userId } }),
  );
  assertOk(res);
  return {
    tasks: res.data?.tasks ?? [],
    issues: res.data?.issues ?? [],
  };
}

export async function reassignProjectOpenItems(
  projectId: number,
  itemIds: number[],
  toUserId: number,
  opts?: {
    notify?: boolean;
    fromUserId?: number;
    transferAssignee?: boolean;
    transferOwner?: boolean;
  },
): Promise<MemberOpenTaskReassignResult> {
  const res = await request<ApiResult<MemberOpenTaskReassignResult>>(
    http.post(`${workflowBase(projectId)}/reassign-open-items`, {
      itemIds,
      toUserId,
      notify: opts?.notify,
      fromUserId: opts?.fromUserId,
      transferAssignee: opts?.transferAssignee,
      transferOwner: opts?.transferOwner,
    }),
  );
  assertOk(res);
  return (res.data as MemberOpenTaskReassignResult) ?? { reassignedCount: 0 };
}

export async function notifyManualReassignAssignees(
  projectId: number,
  recipients: Array<{ userId: number; itemTitles?: string[] }>,
): Promise<void> {
  const res = await request<ApiResult<unknown>>(
    http.post(`${workflowBase(projectId)}/notify-manual-reassign`, { recipients }),
  );
  assertOk(res);
}

export type LlmReassignScope = 'REMOVED_MEMBER' | 'FULL_OPEN_WBS' | 'ORPHAN_WBS_OF_USER';

export interface LlmReassignTasksResult {
  total: number;
  reassigned: number;
  fallbackCount: number;
  skipped: number;
  usedLlm: boolean;
  summary?: string | null;
  samples?: string[] | null;
}

export async function reassignTasksByLlm(
  projectId: number,
  payload: {
    scope: LlmReassignScope;
    fromUserId?: number | null;
    useLlm?: boolean;
    changeReason?: string | null;
    addedMemberUserIds?: number[] | null;
    removedMemberUserIds?: number[] | null;
    changedPhaseCode?: string | null;
    previousPhaseOwnerUserId?: number | null;
    newPhaseOwnerUserId?: number | null;
  },
): Promise<LlmReassignTasksResult> {
  const res = await request<ApiResult<LlmReassignTasksResult>>(
    // 可能调用大模型；默认 60s 易在全量 WBS 场景超时
    http.post(`${workflowBase(projectId)}/reassign-tasks-by-llm`, payload, { timeout: 180_000 }),
  );
  assertOk(res);
  return (
    res.data ?? {
      total: 0,
      reassigned: 0,
      fallbackCount: 0,
      skipped: 0,
      usedLlm: false,
      summary: res.message,
    }
  );
}

/** AI 帮写项目简介结果 */
export interface ProjectIntroRewriteResult {
  projectDescription?: string | null;
}

export async function submitProjectIntroRewrite(projectId: number): Promise<ProjectIntroRewriteResult> {
  const res = await request<ApiResult<ProjectIntroRewriteResult>>(
    http.post(`${workflowBase(projectId)}/intro-rewrite`),
  );
  assertOk(res);
  return res.data ?? {};
}

/** TR 阶段门：发起后大模型建议与待办 id */
export interface TrReviewSubmitResult {
  recommendPass?: boolean | null;
  reason?: string | null;
  potentialImpact?: string | null;
  improvementSuggestions?: string[] | null;
  verdictLabel?: string | null;
  canPushToNextPhase?: boolean | null;
  pushed?: boolean | null;
  reviewTaskId?: number | null;
  reviewTaskTitle?: string | null;
  projectCloseReview?: boolean | null;
  ruleDocumentTitle?: string | null;
  knowledgeDocumentId?: string | null;
  knowledgeBaseId?: string | null;
  checklistSummary?: TrReviewChecklistSummary | null;
  checklistItems?: TrReviewChecklistItemResult[] | null;
  keyMissingSummary?: string | null;
  reviewer1UserId?: number | null;
  reviewer1Name?: string | null;
  reviewer2UserId?: number | null;
  reviewer2Name?: string | null;
}

export interface TrReviewChecklistSummary {
  total?: number;
  submitted?: number;
  missing?: number;
  undetermined?: number;
}

export interface TrReviewChecklistItemResult {
  deliverableName?: string;
  auditResult?: string;
  evidenceSource?: string;
  aiNote?: string;
  matchedFileName?: string;
}

export interface TrReviewPhaseValidation {
  phaseCode?: string;
  systemPhaseName?: string;
  recognizedStandardPhase?: string;
  validationResult?: 'success' | 'failed' | 'unknown' | string;
  currentPhase?: boolean;
}

export interface TrReviewPrepareResult {
  ruleDocumentTitle?: string;
  knowledgeDocumentId?: string;
  knowledgeBaseId?: string;
  phaseValidations?: TrReviewPhaseValidation[];
  currentPhaseChecklist?: { itemNo?: string; deliverableName?: string; tableCode?: string }[];
  canSubmit?: boolean;
  fromCache?: boolean;
}

export interface TrReviewEligibility {
  showButton: boolean;
  canSubmit: boolean;
  canPush: boolean;
  /** 可丢弃未推送草稿并重新 AI 评审 */
  canResubmit?: boolean | null;
  reason?: string | null;
  pendingRecommendPass?: boolean | null;
  pendingReason?: string | null;
  pendingVerdictLabel?: string | null;
  pendingPotentialImpact?: string | null;
  pendingImprovementSuggestions?: string[] | null;
}

export async function getTrReviewEligibility(projectId: number): Promise<TrReviewEligibility> {
  const res = await request<ApiResult<TrReviewEligibility>>(
    http.get(`${workflowBase(projectId)}/tr-review/eligibility`),
  );
  assertOk(res);
  return (
    res.data ?? {
      showButton: false,
      canSubmit: false,
      canPush: false,
    }
  );
}

export async function getPendingTrReviewResult(projectId: number): Promise<TrReviewSubmitResult> {
  const res = await request<ApiResult<TrReviewSubmitResult>>(
    http.get(`${workflowBase(projectId)}/tr-review/pending-result`),
  );
  assertOk(res);
  return res.data ?? {};
}

export async function listMyPendingTrReviewTasks(projectId: number): Promise<TaskItem[]> {
  const res = await request<ApiResult<TaskItem[]>>(
    http.get(`${workflowBase(projectId)}/tr-review/pending-tasks`),
  );
  assertOk(res);
  return res.data ?? [];
}

export async function prepareTrReviewChecklist(
  projectId: number,
  payload: { knowledgeDocumentId: string; knowledgeBaseId: string },
): Promise<TrReviewPrepareResult> {
  const res = await request<ApiResult<TrReviewPrepareResult>>(
    http.post(`${workflowBase(projectId)}/tr-review/prepare-checklist`, payload),
  );
  assertOk(res);
  return res.data ?? {};
}

export async function submitTrPhaseReview(
  projectId: number,
  options?: { forceRetry?: boolean; knowledgeDocumentId?: string; knowledgeBaseId?: string; reviewer1UserId?: number | null; reviewer2UserId?: number | null },
): Promise<TrReviewSubmitResult> {
  const forceRetry = Boolean(options?.forceRetry);
  const body =
    options?.knowledgeDocumentId || options?.knowledgeBaseId || options?.reviewer1UserId || options?.reviewer2UserId
      ? {
          knowledgeDocumentId: options?.knowledgeDocumentId,
          knowledgeBaseId: options?.knowledgeBaseId,
          reviewer1UserId: options?.reviewer1UserId ?? null,
          reviewer2UserId: options?.reviewer2UserId ?? null,
        }
      : undefined;
  const res = await request<ApiResult<TrReviewSubmitResult>>(
    http.post(`${workflowBase(projectId)}/tr-review/submit`, body ?? null, {
      params: forceRetry ? { forceRetry: true } : undefined,
    }),
  );
  assertOk(res);
  return res.data ?? {};
}

export async function pushTrPhaseReview(projectId: number): Promise<TrReviewSubmitResult> {
  const res = await request<ApiResult<TrReviewSubmitResult>>(
    http.post(`${workflowBase(projectId)}/tr-review/push`),
  );
  assertOk(res);
  return res.data ?? {};
}

export async function decideTrPhaseReview(
  projectId: number,
  payload: { taskId: number; approved: boolean; remark?: string | null },
): Promise<void> {
  const res = await request<ApiResult<unknown>>(http.post(`${workflowBase(projectId)}/tr-review/decide`, payload));
  assertOk(res);
}

export interface ProjectVersionHistoryItem {
  verMajor: number;
  verMinor: number;
  verPatch?: number;
  semanticVersion: string;
  createdAt?: string | null;
  /** 是否为项目当前最新版本（未必有快照） */
  current?: boolean;
}

export async function getProjectVersionHistory(projectId: number): Promise<ProjectVersionHistoryItem[]> {
  const res = await request<ApiResult<ProjectVersionHistoryItem[]>>(
    http.get(`${workflowBase(projectId)}/version-history`),
  );
  assertOk(res);
  return res.data ?? [];
}

export async function getChangeRequestEligibility(projectId: number): Promise<boolean> {
  const res = await request<ApiResult<boolean>>(http.get(`${workflowBase(projectId)}/change-request/eligibility`));
  assertOk(res);
  return Boolean(res.data);
}

export async function submitChangeRequest(
  projectId: number,
  payload: { reason: string; targetPhaseCode: string; attachmentKeys?: string[] },
): Promise<number> {
  const res = await request<ApiResult<number>>(http.post(`${workflowBase(projectId)}/change-request/submit`, payload));
  assertOk(res);
  return typeof res.data === 'number' ? res.data : 0;
}

export async function decideChangeRequest(
  projectId: number,
  payload: { changeRequestId?: number; taskId?: number; approved: boolean; remark?: string | null },
): Promise<void> {
  const res = await request<ApiResult<unknown>>(http.post(`${workflowBase(projectId)}/change-request/decide`, payload));
  assertOk(res);
}

export interface ProjectWbsSnapshot {
  exists: boolean;
  generatedAt?: string | null;
  content?: unknown;
  canEdit?: boolean;
}

/** 对齐外部 WBS API project_info */
export interface ProjectWbsProjectInfo {
  customer?: string;
  platform?: string;
  vehicleType?: string;
  lifecycleDuration?: string;
  keyTech?: string[];
  industry?: string;
}

/** 对齐外部 WBS API project_snapshot.risks[] */
export interface ProjectWbsSnapshotRisk {
  name?: string;
  desc?: string;
}

/** 对齐外部 WBS API project_snapshot */
export interface ProjectWbsProjectSnapshot {
  background?: string;
  scope?: string;
  deliverablesSummary?: string;
  risks?: ProjectWbsSnapshotRisk[];
  stakeholders?: string;
  assumptions?: string;
}

/** WBS 生成选项，对齐 POST /api_v1/wbs/external/generate */
export interface GenerateProjectWbsOptions {
  projectType?: string;
  productType?: string;
  projectInfo?: ProjectWbsProjectInfo;
  stageTemplate?: string;
  summary?: string;
  referenceTags?: string[];
  generationMode?: 'auto' | 'template_first' | 'knowledge_first';
  constraints?: Record<string, string>;
  milestoneDates?: Record<string, string>;
  projectSnapshot?: ProjectWbsProjectSnapshot;
  /** 模板「工时配置」表行（REQ-04） */
  hoursConfigRows?: Array<Record<string, unknown>>;
  /** 工时分配结果（needsConfirm / assignments）；不阻断生成 */
  hoursPlan?: {
    needsConfirm?: boolean;
    reason?: string;
    assignments?: Array<Record<string, unknown>>;
    [key: string]: unknown;
  };
  /** 知识库 WBS 模板引用（可选） */
  wbsTemplateRef?: WbsTemplateRef | null;
}

export type ProjectWbsGenerateContext = GenerateProjectWbsOptions;

export async function listWbsTemplates(): Promise<WbsTemplateCandidate[]> {
  const res = await request<ApiResult<WbsTemplateCandidate[]>>(http.get('/wbs/templates'));
  assertOk(res);
  return Array.isArray(res.data) ? res.data : [];
}

export async function saveProjectWbsGenerateContext(
  projectId: number,
  body: GenerateProjectWbsOptions,
): Promise<ProjectWbsGenerateContext> {
  const res = await request<ApiResult<ProjectWbsGenerateContext>>(
    http.put(`/projects/${projectId}/wbs/generate-context`, body),
  );
  assertOk(res);
  return res.data ?? {};
}

export interface WbsIpdTemplate {
  phases?: Array<{
    name?: string;
    description?: string;
    gate?: string;
    children?: Array<{ title?: string; owner?: string; description?: string }>;
  }>;
  total_phases?: number;
  total_children?: number;
}

export async function getProjectWbsSnapshot(projectId: number): Promise<ProjectWbsSnapshot> {
  const res = await request<ApiResult<ProjectWbsSnapshot>>(http.get(`/projects/${projectId}/wbs`));
  assertOk(res);
  return res.data ?? { exists: false };
}

export async function getProjectWbsGenerateContext(projectId: number): Promise<ProjectWbsGenerateContext> {
  const res = await request<ApiResult<ProjectWbsGenerateContext>>(
    http.get(`/projects/${projectId}/wbs/generate-context`),
  );
  assertOk(res);
  return res.data ?? {};
}

export async function getProjectWbsIpdTemplate(projectId: number): Promise<WbsIpdTemplate> {
  const res = await request<ApiResult<WbsIpdTemplate>>(http.get(`/projects/${projectId}/wbs/ipd-template`));
  assertOk(res);
  return res.data ?? {};
}

export async function generateProjectWbsSnapshot(projectId: number): Promise<ProjectWbsSnapshot> {
  const res = await request<ApiResult<ProjectWbsSnapshot>>(
    http.post(`/projects/${projectId}/wbs/generate`, {}, { timeout: PROJECT_WBS_HTTP_TIMEOUT_MS }),
  );
  assertOk(res);
  return res.data ?? { exists: false };
}

export interface ProjectWbsGenerateTaskStep {
  key?: string;
  title?: string;
  status?: string;
  text?: string;
}

export interface ProjectWbsGenerateTaskResult {
  status?: string;
  currentStep?: string;
  message?: string;
  progress?: number;
  steps?: ProjectWbsGenerateTaskStep[];
  snapshot?: ProjectWbsSnapshot | null;
  syncedTaskCount?: number | null;
}

export async function submitProjectWbsGenerateTask(projectId: number): Promise<{ taskId: string }> {
  const res = await request<ApiResult<{ taskId?: string }>>(
    http.post(`/projects/${projectId}/wbs/generate/task`, {}),
  );
  assertOk(res);
  const taskId = String(res.data?.taskId || '').trim();
  if (!taskId) throw new Error('未返回 WBS 生成任务 ID');
  return { taskId };
}

export async function getProjectWbsGenerateTask(
  projectId: number,
  taskId: string,
): Promise<ProjectWbsGenerateTaskResult> {
  const res = await request<ApiResult<ProjectWbsGenerateTaskResult>>(
    http.get(`/projects/${projectId}/wbs/generate/task/${encodeURIComponent(taskId)}`, {
      timeout: 60_000,
    }),
  );
  assertOk(res);
  return res.data ?? {};
}

export async function cancelProjectWbsGenerateTask(
  projectId: number,
  taskId: string,
): Promise<ProjectWbsGenerateTaskResult> {
  const res = await request<ApiResult<ProjectWbsGenerateTaskResult>>(
    http.post(`/projects/${projectId}/wbs/generate/task/${encodeURIComponent(taskId)}/cancel`, {}),
  );
  assertOk(res);
  return res.data ?? {};
}

/** 提交并轮询 WBS 异步生成，直至完成或失败；abort 时通知后端丢弃晚到结果 */
export async function pollProjectWbsGenerateTask(
  projectId: number,
  options?: {
    intervalMs?: number;
    signal?: AbortSignal;
    /** 停止时调用后端 cancel，避免三方结果落库（默认 true） */
    discardOnAbort?: boolean;
    onProgress?: (data: ProjectWbsGenerateTaskResult) => void;
  },
): Promise<ProjectWbsGenerateTaskResult> {
  const { taskId } = await submitProjectWbsGenerateTask(projectId);
  const intervalMs = options?.intervalMs ?? 1200;
  const signal = options?.signal;
  const discardOnAbort = options?.discardOnAbort !== false;
  const cancelRemote = () => {
    if (!discardOnAbort) return;
    void cancelProjectWbsGenerateTask(projectId, taskId).catch(() => {
      /* ignore */
    });
  };
  if (signal) {
    if (signal.aborted) {
      cancelRemote();
      throw new DOMException('已取消', 'AbortError');
    }
    signal.addEventListener('abort', cancelRemote, { once: true });
  }
  for (;;) {
    if (signal?.aborted) {
      throw new DOMException('已取消', 'AbortError');
    }
    const data = await getProjectWbsGenerateTask(projectId, taskId);
    options?.onProgress?.(data);
    const status = String(data.status || '').toLowerCase();
    if (status === 'failed') {
      throw new Error(data.message || 'WBS 生成失败');
    }
    if (status === 'cancelled') {
      throw new DOMException('已取消', 'AbortError');
    }
    if (status === 'completed') {
      return data;
    }
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => resolve(), intervalMs);
      const onAbort = () => {
        window.clearTimeout(timer);
        reject(new DOMException('已取消', 'AbortError'));
      };
      if (signal) {
        if (signal.aborted) {
          onAbort();
          return;
        }
        signal.addEventListener('abort', onAbort, { once: true });
      }
    });
  }
}

export async function saveProjectWbsSnapshot(projectId: number, content: unknown): Promise<ProjectWbsSnapshot> {
  const res = await request<ApiResult<ProjectWbsSnapshot>>(http.put(`/projects/${projectId}/wbs`, { content }));
  assertOk(res);
  return res.data ?? { exists: false };
}

/** 回退 WBS 生成：恢复 previousContent；传 null/省略则清空快照与任务 */
export async function restoreProjectWbsSnapshot(
  projectId: number,
  previousContent: unknown | null,
): Promise<ProjectWbsSnapshot> {
  const res = await request<ApiResult<ProjectWbsSnapshot>>(
    http.post(`/projects/${projectId}/wbs/restore`, { previousContent }),
  );
  assertOk(res);
  return res.data ?? { exists: false };
}

/** 项目监控弹框聚合数据（与后端 ProjectMonitorDTO 对齐） */
export interface ProjectMonitorNamedCount {
  name: string;
  value: number;
}

export interface ProjectMonitorTokenUserStat {
  userId: number;
  displayName: string;
  department: string;
  value: number;
}

export interface ProjectMonitorDeptBar {
  department: string;
  open: number;
  closed: number;
  delayed: number;
  onTrackOpen: number;
}

export interface ProjectMonitorProblemStatRow {
  severity: string;
  issueCategory?: string | null;
  stage: string;
  total: number;
  resolved: number;
  unresolved: number;
  delayed: number;
  resolutionRate: number;
  hoursSpent?: number | string | null;
  ownerDisplayName?: string | null;
  /** 已关闭问题平均关单时长（创建→关闭） */
  avgCloseDurationLabel?: string | null;
}

export interface ProjectMonitorData {
  problemStats: ProjectMonitorProblemStatRow[];
  departmentProblemBars: ProjectMonitorDeptBar[];
  problemStatusPie: ProjectMonitorNamedCount[];
  tokenUsageByDepartmentPie: ProjectMonitorNamedCount[];
  tokenUsageByUser: ProjectMonitorTokenUserStat[];
}

export async function getProjectMonitor(projectId: number): Promise<ProjectMonitorData> {
  const res = await request<ApiResult<ProjectMonitorData>>(http.get(`/projects/${projectId}/monitor`));
  assertOk(res);
  return res.data ?? {
    problemStats: [],
    departmentProblemBars: [],
    problemStatusPie: [],
    tokenUsageByDepartmentPie: [],
    tokenUsageByUser: [],
  };
}
